import { generateMarkBasedComparison, interleaveComparisonPoints } from '../../features/comparison/comparisonEngine';
import { getTimestampFeature } from '../../features/metricValue';
import { EffectiveDefinition, DataTable } from '../../models';
import { accessorForReference } from '../../models/Accessor';
import { Calculation, ColumnReference } from '../../models/EffectiveDefinition';
import { TransformFunction } from '../transform';
import { CopyEntry, makeOutputFunc } from './TimeShiftCalculation';

export const RowShiftType = 'row-shift';

/**
 * A RowShift transform
 *
 * This transformation is responsible for our interactive historical comparisons feature, but only when the underlying granularity of the metric is unknown..
 *
 * It produces new columns of data, showing the values of the given input columns in the past.
 * Specifically, whatever `amount` of rows in the past.
 *
 * Currently, most of the actual implementation lives src/features/threshold/comparisonEngine.ts
 * because that's where it was initially implemented. We should move that implementation here in the future.
 *
 * @param amount The number of rows in the past
 * @param source The column to use when calculating the row shift. Must be a `DateTime` column. Currently always `timestamp`.
 * @param copy An array of pairs of `ColumnReference`s to copy `from` and `to.
 */
export interface RowShiftCalculation {
  type: typeof RowShiftType;
  amount: number;
  source?: ColumnReference;
  copy?: ReadonlyArray<CopyEntry>;
}

const isRowShiftCalculation = (calc?: Calculation): calc is RowShiftCalculation => {
  return calc !== undefined && calc.type === RowShiftType;
};

export const rowShiftTransformCreator =
  (definition: EffectiveDefinition, name: string): TransformFunction =>
  (sourceTable, windowedTable) =>
    rowShiftTransform(definition, name, sourceTable, windowedTable);

export const rowShiftTransform = (
  definition: EffectiveDefinition,
  name: string,
  sourceTable: DataTable,
  windowedTable: DataTable
): DataTable => {
  const calculation = definition.schema[name]?.calculation;
  if (!isRowShiftCalculation(calculation)) {
    throw new Error(`Could not resolve RowShift calculation with name: ${name}`);
  }

  // For now, `RowShift` is implicitly coupled to the `timestamp` feature.
  // This is due to historical implementation reasons, and it should be relatively
  // straightforward to remove this limitation in the future.
  const timeColumnRef = calculation.source ?? getTimestampFeature(definition.features);
  const timestampAccessor = accessorForReference<Date>(timeColumnRef);

  const outputFunc = makeOutputFunc(definition, name, calculation);
  const comparisonPoints = generateMarkBasedComparison(
    sourceTable,
    windowedTable,
    calculation.amount,
    timestampAccessor,
    outputFunc
  );
  return interleaveComparisonPoints(windowedTable, comparisonPoints, timestampAccessor);
};
